import type { TimesheetEntry, TimeStats } from "./types"

// Helper functions to handle date conversion
export const dateToString = (date: Date): string => {
  return date.toISOString()
}

export const stringToDate = (dateStr: string): Date => {
  return new Date(dateStr)
}

export const ensureDateObject = (date: string | Date): Date => {
  return typeof date === 'string' ? new Date(date) : date
}

export const ensureDateString = (date: string | Date): string => {
  return typeof date === 'string' ? date : date.toISOString()
}

export function calculateDuration(start: Date | string, end: Date | string | null): number {
  if (!end) return 0
  const startDate = ensureDateObject(start)
  const endDate = ensureDateObject(end)
  return Math.floor((endDate.getTime() - startDate.getTime()) / 1000 / 60)
}

export function calculateDurationInSeconds(start: Date | string, end: Date | string | null): number {
  if (!end) return 0
  const startDate = ensureDateObject(start)
  const endDate = ensureDateObject(end)
  return Math.floor((endDate.getTime() - startDate.getTime()) / 1000)
}

export function formatDuration(minutes: number, showSeconds = false): string {
  if (showSeconds) {
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    const secs = Math.floor((minutes * 60) % 60)

    if (hours === 0 && mins === 0) return `${secs}s`
    if (hours === 0) return `${mins}m ${secs}s`
    return `${hours}h ${mins}m ${secs}s`
  }

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

export function formatDurationFromSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours === 0 && mins === 0) return `${secs}s`
  if (hours === 0) return `${mins}m ${secs}s`
  return `${hours}h ${mins}m ${secs}s`
}

export function formatTime(date: Date | string): string {
  const dateObj = ensureDateObject(date)
  return dateObj.toLocaleTimeString("en-US", { 
    hour: "2-digit", 
    minute: "2-digit",
    hour12: true 
  })
}

export function formatDate(date: Date | string): string {
  const dateObj = ensureDateObject(date)
  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  })
}

export function calculateStats(entries: TimesheetEntry[]): TimeStats {
  const stats: TimeStats = {
    totalMinutes: 0,
    byTask: {},
    byDay: {},
    byWeek: {},
    byMonth: {},
  }

  entries.forEach((entry) => {
    const duration = calculateDuration(entry.startTime, entry.endTime || null) + (entry.manualMinutes || 0)
    stats.totalMinutes += duration

    // By task
    if (!stats.byTask[entry.taskId]) {
      stats.byTask[entry.taskId] = 0
    }
    stats.byTask[entry.taskId] += duration

    // By day
    const dayKey = ensureDateString(entry.startTime).split("T")[0]
    if (!stats.byDay[dayKey]) {
      stats.byDay[dayKey] = 0
    }
    stats.byDay[dayKey] += duration

    // By week
    const startDate = ensureDateObject(entry.startTime)
    const weekKey = getISOWeek(startDate)
    if (!stats.byWeek[weekKey]) {
      stats.byWeek[weekKey] = 0
    }
    stats.byWeek[weekKey] += duration

    // By month
    const monthKey = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}`
    if (!stats.byMonth[monthKey]) {
      stats.byMonth[monthKey] = 0
    }
    stats.byMonth[monthKey] += duration
  })

  return stats
}

function getISOWeek(date: Date): string {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 4 - (d.getDay() || 7))
  const yearStart = new Date(d.getFullYear(), 0, 1)
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${d.getFullYear()}-W${String(weekNo).padStart(2, "0")}`
}
